import { useCallback, useEffect, useMemo, useRef } from 'react';
import PackagesApi from 'legacyfrontend/api/paquetes';
import { CACHE_DEFAULTS } from 'legacyfrontend/constants';
import { getCacheStatus, setCacheStatus } from 'legacyfrontend/helpers/getCacheStatus';
import { getFlightSearchState } from 'legacyfrontend/helpers/flightSearchStateStorage';
import { validateFlightsSearchFromUrlParams } from 'legacyfrontend/helpers/validateFlightsSearchSubmit';
import {
  getEssentialFlightSearchKey,
  hasRequiredFlightSearchParams,
  parseFlightSearchParams,
  stripNonSearchParams,
} from 'legacyfrontend/flights/search/flightSearchParams';
import {
  buildLlegoPayload,
  getFlightSearchReturnDate,
  isOneWayFlightSearch,
} from 'legacyfrontend/flights/search/flightSearchPayload';
import { saveLastEssentialSearchParams } from 'legacyfrontend/flights/search/flightSearchStorage';

const isSupportedCurrency = (currency) => currency === 'USD' || currency === 'ARS';

const getFlightSearchErrorMessage = (error) => {
  if (error?.response?.status === 500) {
    return 'Error del servidor. Por favor, intenta nuevamente en unos momentos.';
  }

  if (error?.response?.status === 404) {
    return 'No se encontraron vuelos con los criterios especificados.';
  }

  if (error?.code === 'ERR_NETWORK') {
    return 'Error de conexión. Verifica tu conexión a internet.';
  }

  return error?.message || 'Error al cargar los vuelos';
};

const getLastEssentialParams = () => {
  try {
    return getFlightSearchState().lastEssentialSearchParams || '';
  } catch (error) {
    return '';
  }
};

const setLastEssentialParams = (params) => {
  try {
    saveLastEssentialSearchParams(params || '');
  } catch (error) {
    console.warn('Error saving lastEssentialSearchParams to flightSearchState:', error);
  }
};

export const useFlightResultsFetch = ({
  delegation,
  filters,
  handleCacheExpiration,
  handleFilters,
  location,
  onSearchCount,
  registerOnSearch,
  setData,
  setError,
  setFlightContextLoading,
  setFlightInformation,
  setIsLoading,
  setOneWayFlight,
  showError,
  isLoading,
}) => {
  const search = location?.search || '';
  const hasCalledApiRef = useRef(false);
  const isCallingApiRef = useRef(false);
  const prevCurrencyRef = useRef(filters.currency);
  const prevCabinClassRef = useRef(filters.cabinClass);
  const isInitialCurrencySyncRef = useRef(false);
  const currencyChangeFromUrlRef = useRef(false);
  const prevRefreshParamRef = useRef(null);
  const searchParamsWithoutRefreshRef = useRef(null);

  const searchParamsToObject = useMemo(() => parseFlightSearchParams(search), [search]);
  const searchWithoutRefresh = useMemo(() => stripNonSearchParams(search), [search]);
  const hasValidSearchParams = useMemo(
    () => hasRequiredFlightSearchParams(searchParamsToObject),
    [searchParamsToObject]
  );

  const handleLlegoData = useCallback(
    async ({ useUrlCurrency = true } = {}) => {
      if (!hasValidSearchParams) {
        setData([]);
        setIsLoading(false);
        setFlightContextLoading(false);
        setError(false);
        isCallingApiRef.current = false;
        return;
      }

      setError(false);

      const {
        destinationId,
        destinationName,
        originId,
        originName,
        departureDate,
        passengers,
        currency: urlCurrency,
        isOnlyOneWay: urlIsOnlyOneWay,
      } = searchParamsToObject;

      if (getCacheStatus() === true) {
        setCacheStatus('true');
      }

      const cacheValue = getCacheStatus();
      const currency = useUrlCurrency && isSupportedCurrency(urlCurrency)
        ? urlCurrency
        : filters.currency;
      const isOnlyOneWay = isOneWayFlightSearch(urlIsOnlyOneWay);
      const cleanedReturnDate = getFlightSearchReturnDate(searchParamsToObject);

      setFlightInformation((prevFlightInfo) => {
        if (isOnlyOneWay !== prevFlightInfo?.isOnlyOneWay) {
          setOneWayFlight(isOnlyOneWay);
        }

        return {
          ...prevFlightInfo,
          destinationId,
          destinationName,
          originId,
          originName,
          departureDate,
          returnDate: cleanedReturnDate,
          passengers,
          isOnlyOneWay,
          cabinClass: filters.cabinClass,
          currency,
        };
      });

      const data = buildLlegoPayload({
        params: searchParamsToObject,
        filters,
        cache: cacheValue,
        delegation,
        currency,
      });

      const urlValidationError = validateFlightsSearchFromUrlParams({
        originId,
        originName,
        destinationId,
        destinationName,
        departureDate,
        returnDate: cleanedReturnDate,
        isOnlyOneWay,
        passengers,
      });

      if (urlValidationError) {
        setError(true);
        setData([]);
        setIsLoading(false);
        setFlightContextLoading(false);
        isCallingApiRef.current = false;
        showError(urlValidationError.message);
        return;
      }

      try {
        setIsLoading(true);
        setFlightContextLoading(true);
        const dataResponse = await PackagesApi.fetchLlegoData(data);
        setData(dataResponse?.data || []);
        handleCacheExpiration(
          dataResponse?.cacheExpiresAt ||
          new Date(Date.now() + CACHE_DEFAULTS.DEFAULT_EXPIRATION_TIME)
        );
        setCacheStatus('true');
        setError(false);

        if (useUrlCurrency && isSupportedCurrency(urlCurrency) && filters.currency !== urlCurrency) {
          handleFilters('currency', urlCurrency);
          prevCurrencyRef.current = urlCurrency;
        }
      } catch (error) {
        showError(getFlightSearchErrorMessage(error));
        setError(true);
        setData([]);
      } finally {
        setIsLoading(false);
        setFlightContextLoading(false);
        isCallingApiRef.current = false;
      }
    },
    [
      hasValidSearchParams,
      searchParamsToObject,
      filters,
      delegation,
      setFlightInformation,
      handleCacheExpiration,
      setOneWayFlight,
      setData,
      setIsLoading,
      setFlightContextLoading,
      setError,
      showError,
      handleFilters,
    ]
  );

  const handleLlegoDataRef = useRef(handleLlegoData);

  useEffect(() => {
    handleLlegoDataRef.current = handleLlegoData;
  }, [handleLlegoData]);

  useEffect(() => {
    if (!searchWithoutRefresh) return;

    const paramsWithoutCurrency = new URLSearchParams(searchWithoutRefresh);
    paramsWithoutCurrency.delete('currency');
    const searchWithoutCurrency = paramsWithoutCurrency.toString();

    const prevParamsWithoutCurrency = searchParamsWithoutRefreshRef.current
      ? new URLSearchParams(searchParamsWithoutRefreshRef.current)
      : new URLSearchParams();
    prevParamsWithoutCurrency.delete('currency');
    const prevSearchWithoutCurrency = prevParamsWithoutCurrency.toString();

    const currentEssential = getEssentialFlightSearchKey(searchWithoutRefresh);
    const prevEssential = getEssentialFlightSearchKey(searchParamsWithoutRefreshRef.current || '');
    const lastEssential = getLastEssentialParams();

    const onlyCurrencyChanged = searchParamsWithoutRefreshRef.current !== null &&
      prevSearchWithoutCurrency === searchWithoutCurrency &&
      searchParamsWithoutRefreshRef.current !== searchWithoutRefresh;
    const essentialParamsUnchanged = prevEssential !== '' && currentEssential === prevEssential;
    const isReturningWithSameParams = prevEssential === '' &&
      currentEssential !== '' &&
      currentEssential === lastEssential &&
      searchParamsWithoutRefreshRef.current !== null &&
      hasCalledApiRef.current;
    const searchParamsChanged = !essentialParamsUnchanged && !isReturningWithSameParams;

    if (searchParamsChanged) {
      hasCalledApiRef.current = false;
      searchParamsWithoutRefreshRef.current = searchWithoutRefresh;
      setLastEssentialParams(currentEssential);
      currencyChangeFromUrlRef.current = false;
    } else if (onlyCurrencyChanged) {
      currencyChangeFromUrlRef.current = true;
      searchParamsWithoutRefreshRef.current = searchWithoutRefresh;

      const urlCurrency = searchParamsToObject.currency;
      if (isSupportedCurrency(urlCurrency)) {
        prevCurrencyRef.current = urlCurrency;
        handleFilters('currency', urlCurrency);
      }
    } else if (searchParamsWithoutRefreshRef.current !== searchWithoutRefresh) {
      searchParamsWithoutRefreshRef.current = searchWithoutRefresh;
      if (currentEssential) {
        setLastEssentialParams(currentEssential);
      }
    }
  }, [searchWithoutRefresh, searchParamsToObject.currency, filters.currency, handleFilters]);

  const handleRetry = useCallback(async () => {
    if (!hasValidSearchParams) return;

    await handleLlegoData({ useUrlCurrency: false });
  }, [hasValidSearchParams, handleLlegoData]);

  useEffect(() => {
    // isLoading starts true. A client-side search increments onSearchCount
    // before FlightsFiltersProvider mounts, so the reset that would clear
    // that flag never runs. Guarding on isLoading then skips the request
    // and the spinner stays up. isCallingApiRef covers the in-flight case.
    // isLoading stays in the deps so a search that arrives mid-request is
    // picked up when that request finishes.
    if (!searchWithoutRefresh || !hasValidSearchParams || isCallingApiRef.current) {
      return;
    }

    const currentRefreshParam = searchParamsToObject._refresh;
    const searchParamsChanged = searchParamsWithoutRefreshRef.current !== null &&
      searchParamsWithoutRefreshRef.current !== searchWithoutRefresh;
    const newRefreshParam = currentRefreshParam && currentRefreshParam !== prevRefreshParamRef.current;
    const shouldFetchInitialSearch = !currentRefreshParam && !hasCalledApiRef.current;
    const shouldFetchRefresh = newRefreshParam ||
      (searchParamsWithoutRefreshRef.current === null && currentRefreshParam);

    if (searchParamsChanged && !currentRefreshParam) {
      searchParamsWithoutRefreshRef.current = searchWithoutRefresh;
      hasCalledApiRef.current = false;
      setLastEssentialParams(getEssentialFlightSearchKey(searchWithoutRefresh));
    }

    if (!shouldFetchInitialSearch && !shouldFetchRefresh) {
      return;
    }

    hasCalledApiRef.current = true;
    isCallingApiRef.current = true;
    if (currentRefreshParam) {
      prevRefreshParamRef.current = currentRefreshParam;
    }
    searchParamsWithoutRefreshRef.current = searchWithoutRefresh;

    const urlCurrency = searchParamsToObject.currency;
    prevCurrencyRef.current = isSupportedCurrency(urlCurrency) ? urlCurrency : filters.currency;
    prevCabinClassRef.current = filters.cabinClass;
    isInitialCurrencySyncRef.current = false;
    currencyChangeFromUrlRef.current = false;

    if (onSearchCount === 0) {
      registerOnSearch();
    }

    setLastEssentialParams(getEssentialFlightSearchKey(searchWithoutRefresh));

    handleLlegoDataRef.current().finally(() => {
      isCallingApiRef.current = false;
    });
  }, [
    searchWithoutRefresh,
    searchParamsToObject._refresh,
    searchParamsToObject.currency,
    filters.currency,
    filters.cabinClass,
    hasValidSearchParams,
    isLoading,
    onSearchCount,
    registerOnSearch,
  ]);

  useEffect(() => {
    const currencyChanged = prevCurrencyRef.current !== filters.currency;
    const cabinClassChanged = prevCabinClassRef.current !== filters.cabinClass;

    if (
      hasValidSearchParams &&
      hasCalledApiRef.current &&
      !isCallingApiRef.current &&
      !isLoading &&
      (currencyChanged || cabinClassChanged)
    ) {
      if (currencyChangeFromUrlRef.current && currencyChanged && !cabinClassChanged) {
        currencyChangeFromUrlRef.current = false;
        prevCurrencyRef.current = filters.currency;
        prevCabinClassRef.current = filters.cabinClass;
        handleLlegoData();
        return;
      }

      if (isInitialCurrencySyncRef.current && currencyChanged && !cabinClassChanged) {
        isInitialCurrencySyncRef.current = false;
        prevCurrencyRef.current = filters.currency;
        return;
      }

      isInitialCurrencySyncRef.current = false;
      currencyChangeFromUrlRef.current = false;
      prevCurrencyRef.current = filters.currency;
      prevCabinClassRef.current = filters.cabinClass;
      handleLlegoData();
    }
  }, [
    filters.currency,
    filters.cabinClass,
    hasValidSearchParams,
    handleLlegoData,
    isLoading,
  ]);

  return {
    handleFetch: handleLlegoData,
    handleRetry,
    hasSearched: hasCalledApiRef.current,
    hasValidSearchParams,
    searchParams: searchParamsToObject,
  };
};

export default useFlightResultsFetch;
